<template>
  <div class="budget-revision-page">
    <div v-if="!canUseBudget" class="budget-revision-empty">
      <v-icon size="48" color="#90a4ae">mdi-alert-circle-outline</v-icon>
      <p>사업구분을 선택한 뒤 예산을 작성할 수 있습니다.</p>
    </div>

    <BudgetMainPanel
      v-else
      ref="budgetPanelRef"
      use-member-page-layout
      hide-remote-budget-delete
      :member-code="memberCode"
      :biz-gubun="bizGubun"
      :year="revisionYear"
      :budget-class="selectedBudgetClass"
      @year-change="onPanelYearChange"
      @budget-saved="onBudgetSaved"
      @budget-deleted="onBudgetSaved"
    >
      <template #toolbar>
        <div class="budget-revision-toolbar">
          <div class="bank-tabs-wrapper budget-revision-tabs-wrapper">
            <div class="bank-tabs">
              <button
                v-for="tab in revisionTabs"
                :key="tab.id === '' ? 'main' : tab.id"
                type="button"
                class="bank-tab"
                :class="{ active: selectedRevisionTabId === tab.id }"
                @click="selectRevisionTab(tab.id)"
              >
                <v-icon size="14" class="bank-icon">mdi-file-document-edit-outline</v-icon>
                <span class="bank-alias">{{ tab.label }}</span>
              </button>
              <button
                type="button"
                class="bank-tab budget-revision-manage-tab"
                @click="openRevisionManageDialog"
              >
                <v-icon size="16">mdi-tune-variant</v-icon>
                <span>추경관리</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </BudgetMainPanel>

    <v-dialog
      v-model="showRevisionManageDialog"
      class="budget-page-dialog"
      max-width="520"
      scrollable
    >
      <v-card class="budget-revision-add-card">
        <v-card-title class="budget-revision-add-card__title">
          <v-icon size="22" color="#1e3a5f">mdi-file-document-edit-outline</v-icon>
          <span>추경 관리</span>
          <button
            type="button"
            class="dialog-close-x"
            title="닫기"
            :disabled="addRevisionLoading || deleteRevisionLoading"
            @click="showRevisionManageDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="budget-revision-add-card__body">
          <template v-if="selectedRevisionTabId">
            <p class="budget-revision-add-card__kicker">할 일</p>
            <div class="budget-revision-add-card__choices">
              <button type="button" class="rev-choice" :class="{ 'is-on': revisionManageMode === 'create' }" @click="revisionManageMode = 'create'">
                <span class="rev-choice__mark"></span>
                <span class="rev-choice__text">
                  <strong>추경 새로 만들기</strong>
                  <em>다음 차수 시트를 만듭니다.</em>
                </span>
              </button>
              <button
                type="button"
                class="rev-choice"
                :class="{ 'is-on': revisionManageMode === 'delete' }"
                :disabled="!canDeleteCurrent"
                @click="revisionManageMode = 'delete'"
              >
                <span class="rev-choice__mark"></span>
                <span class="rev-choice__text">
                  <strong>현재 추경 삭제</strong>
                  <em v-if="canDeleteCurrent">지금 보고 있는 추경 시트를 지웁니다.</em>
                  <em v-else>마지막 차수({{ lastRevisionLabel }})만 지울 수 있습니다.</em>
                </span>
              </button>
            </div>
          </template>

          <template v-if="revisionManageMode === 'create'">
            <p class="budget-revision-add-card__kicker">{{ nextBudClass }}차 추경</p>
            <p class="budget-revision-add-card__lead">{{ previousSheetLabel }}을 그대로 둘지, 빈 시트로 시작할지 고릅니다. 그대로 두면 적혀 있던 항목만 옮깁니다. 보험·식재료비·수가를 새로 만들어 넣지 않습니다.</p>
            <div class="budget-revision-add-card__choices">
              <button type="button" class="rev-choice" :class="{ 'is-on': addRevisionMode === 'keep' }" @click="addRevisionMode = 'keep'">
                <span class="rev-choice__mark"></span>
                <span class="rev-choice__text">
                  <strong>직전 그대로 두기</strong>
                  <em>{{ previousSheetLabel }}의 금액을 {{ nextBudClass }}차에 그대로 넣습니다.</em>
                </span>
              </button>
              <button type="button" class="rev-choice" :class="{ 'is-on': addRevisionMode === 'empty' }" @click="addRevisionMode = 'empty'">
                <span class="rev-choice__mark"></span>
                <span class="rev-choice__text">
                  <strong>빈 시트로 시작</strong>
                  <em>금액 없이 칸만 만듭니다.</em>
                </span>
              </button>
            </div>
          </template>

          <template v-else-if="selectedRevisionTabId">
            <p v-if="canDeleteCurrent" class="budget-revision-add-card__delete-note">
              <strong>{{ tabLabel(selectedRevisionTabId) }}</strong> 예산 시트와 같은 차수의 입력 내역을 함께 지웁니다. 지운 내용은 되돌릴 수 없습니다. 다시 만들면 같은 차수 번호가 비어 그 번호로 만듭니다.
            </p>
            <p v-else class="budget-revision-add-card__delete-note">
              마지막 차수({{ lastRevisionLabel }})만 지울 수 있습니다. 그 시트를 연 뒤 삭제하세요.
            </p>
          </template>
        </v-card-text>
        <v-card-actions class="budget-revision-add-card__actions">
          <v-spacer />
          <v-btn variant="outlined" :disabled="addRevisionLoading || deleteRevisionLoading" @click="showRevisionManageDialog = false">취소</v-btn>
          <v-btn
            v-if="revisionManageMode === 'create'"
            color="primary"
            variant="elevated"
            :loading="addRevisionLoading"
            @click="confirmAddRevision"
          >
            <v-icon size="18">mdi-check</v-icon>
            만들기
          </v-btn>
          <v-btn
            v-else
            color="error"
            variant="elevated"
            :loading="deleteRevisionLoading"
            :disabled="!canDeleteCurrent"
            @click="confirmDeleteCurrentRevision"
          >
            <v-icon size="18">mdi-delete</v-icon>
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="revisionSnack.show"
      :timeout="revisionSnack.warnings.length ? 16000 : 5000"
      color="#1e3a5f"
      location="top"
      class="budget-revision-snackbar"
    >
      <div class="budget-revision-snack-body">
        <p class="budget-revision-snack-msg">{{ revisionSnack.message }}</p>
        <ul v-if="revisionSnack.warnings.length" class="budget-revision-snack-warnings">
          <li v-for="(w, i) in revisionSnack.warnings" :key="i">{{ w }}</li>
        </ul>
      </div>
      <template #actions>
        <v-btn variant="text" class="text-white" @click="revisionSnack.show = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch, onMounted, type Ref } from "vue";
import { usePageTabNav } from "@/composables/usePageTabNav";
import { useRoute } from "vue-router";
import BudgetMainPanel from "@/components/budget/BudgetMainPanel.vue";
import { budgetApi } from "@/services/api";

const route = useRoute();

const memberDataRef = inject<Ref<{ m_code?: string } | null>>("memberData");
const selectedBizGubunRef = inject<Ref<string>>("selectedBizGubun");

const memberCode = computed(() => {
  const fromRoute = String(route.params.memberCode || "").trim();
  if (fromRoute) return fromRoute;
  const m = memberDataRef?.value?.m_code;
  return m != null && String(m).trim() ? String(m) : "";
});

const bizGubun = computed(() => selectedBizGubunRef?.value || "");

const canUseBudget = computed(() => Boolean(memberCode.value) && Boolean(bizGubun.value));

const revisionYear = ref(String(new Date().getFullYear()));
const revisionTabs = ref<{ id: string; label: string }[]>([{ id: "", label: "본예산" }]);
const selectedRevisionTabId = ref<string>("");
/** DB에 행이 있는 차수(빈 로컬 탭과 구분 — 삭제·직전복사에 사용) */
const savedBudClassIds = ref<Set<string>>(new Set([""]));

const selectedBudgetClass = computed(() => selectedRevisionTabId.value);

const budgetPanelRef = ref<{ confirmDiscard: () => Promise<boolean> } | null>(null);
const showRevisionManageDialog = ref(false);
const revisionManageMode = ref<"create" | "delete">("create");
const addRevisionMode = ref<"keep" | "empty">("keep");
const addRevisionLoading = ref(false);
const deleteRevisionLoading = ref(false);

const revisionSnack = ref({
  show: false,
  message: "",
  warnings: [] as string[],
});

function showRevisionSnack(message: string, warnings: string[] = []) {
  revisionSnack.value = {
    show: true,
    message,
    warnings: warnings.filter(Boolean),
  };
}

function onPanelYearChange(y: string) {
  revisionYear.value = y;
}

function tabLabel(budClass: string): string {
  const c = budClass != null ? String(budClass) : "";
  if (!c || c === "0") return "본예산";
  return `${c}차 추경`;
}

function normalizeBudId(c: string | null | undefined): string {
  const s = c != null ? String(c) : "";
  return s === "0" ? "" : s;
}

/** 저장된(DB) 추경 중 가장 큰 차수 — 빈 로컬 탭 제외 */
const maxSavedNumericRevisionId = computed(() => {
  const nums = [...savedBudClassIds.value]
    .filter((id) => id !== "")
    .map((id) => parseInt(id, 10))
    .filter((n) => !Number.isNaN(n));
  return nums.length ? Math.max(...nums) : 0;
});

const hasNumericRevision = computed(() => maxSavedNumericRevisionId.value > 0);

/** 다음 차수는 저장된 마지막 번호의 다음. 저장 안 된 빈 탭은 번호를 건너뛰지 않는다. */
const nextBudClass = computed(() => String(maxSavedNumericRevisionId.value + 1));

const lastRevisionLabel = computed(() => {
  if (!hasNumericRevision.value) return "본예산";
  return tabLabel(String(maxSavedNumericRevisionId.value));
});

const previousSheetLabel = computed(() => lastRevisionLabel.value);

/** 저장본의 마지막 차수, 또는 아직 저장하지 않은 다음 차수 빈 탭만 지울 수 있다. */
const canDeleteCurrent = computed(() => {
  const bud = selectedRevisionTabId.value;
  if (!bud) return false;
  if (!savedBudClassIds.value.has(bud)) return bud === nextBudClass.value;
  return bud === String(maxSavedNumericRevisionId.value);
});

async function loadBudTabs() {
  if (!canUseBudget.value) {
    revisionTabs.value = [{ id: "", label: "본예산" }];
    selectedRevisionTabId.value = "";
    savedBudClassIds.value = new Set([""]);
    return;
  }
  try {
    const res = await budgetApi.getBudClasses({
      memberCode: memberCode.value,
      year: revisionYear.value,
      bizGubun: bizGubun.value,
    });
    const raw = (res.data as { data?: string[] }).data;
    const fromApi = Array.isArray(raw) ? raw.map((x) => normalizeBudId(x)) : [""];
    const uniq = [...new Set(fromApi.length ? fromApi : [""])].sort((a, b) => {
      if (a === "") return -1;
      if (b === "") return 1;
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return a.localeCompare(b, "ko");
    });
    const fromApiTabs = uniq.map((id) => ({ id, label: tabLabel(id) }));
    const apiIds = new Set(fromApiTabs.map((t) => t.id));
    savedBudClassIds.value = apiIds;
    const savedNums = [...apiIds]
      .filter((id) => id !== "")
      .map((id) => parseInt(id, 10))
      .filter((n) => !Number.isNaN(n));
    const pendingId = String((savedNums.length ? Math.max(...savedNums) : 0) + 1);
    const localOnly = revisionTabs.value.filter((t) => t.id === pendingId && !apiIds.has(t.id));
    revisionTabs.value = [...fromApiTabs, ...localOnly].sort((a, b) => {
      if (a.id === "") return -1;
      if (b.id === "") return 1;
      const na = parseInt(a.id, 10);
      const nb = parseInt(b.id, 10);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return a.id.localeCompare(b.id, "ko");
    });
    const cur = selectedRevisionTabId.value;
    if (!revisionTabs.value.some((t) => t.id === cur)) {
      selectedRevisionTabId.value = "";
    }
  } catch {
    revisionTabs.value = [{ id: "", label: "본예산" }];
    selectedRevisionTabId.value = "";
    savedBudClassIds.value = new Set([""]);
  }
}

async function selectRevisionTab(id: string) {
  if (id === selectedRevisionTabId.value) return;
  if (budgetPanelRef.value && !(await budgetPanelRef.value.confirmDiscard())) return;
  selectedRevisionTabId.value = id;
}

const budgetPageTabs = computed(() =>
  revisionTabs.value.map((tab) => ({ key: tab.id, title: tab.label }))
);
usePageTabNav("budget-revision", budgetPageTabs, selectedRevisionTabId, selectRevisionTab);

async function addEmptyRevisionTabOnly() {
  const next = nextBudClass.value;
  if (budgetPanelRef.value && !(await budgetPanelRef.value.confirmDiscard())) return;
  if (revisionTabs.value.some((t) => t.id === next)) {
    selectedRevisionTabId.value = next;
    showRevisionSnack(`${next}차 추경 빈 시트가 이미 열려 있습니다. 입력 후 저장하세요.`);
    return;
  }
  revisionTabs.value.push({ id: next, label: `${next}차 추경` });
  revisionTabs.value.sort((a, b) => {
    if (a.id === "") return -1;
    if (b.id === "") return 1;
    return parseInt(a.id, 10) - parseInt(b.id, 10);
  });
  selectedRevisionTabId.value = next;
  showRevisionSnack(`${next}차 추경 빈 시트가 추가되었습니다. 입력 후 저장하세요.`);
}

function openRevisionManageDialog() {
  if (!canUseBudget.value) return;
  revisionManageMode.value = "create";
  addRevisionMode.value = "keep";
  showRevisionManageDialog.value = true;
}

async function confirmDeleteCurrentRevision() {
  const bud = selectedRevisionTabId.value;
  if (!bud || !canDeleteCurrent.value) return;
  deleteRevisionLoading.value = true;
  try {
    // 저장 안 된 빈 시트: 서버 차수 가드(나중 차수 존재)에 막히지 않도록 로컬만 닫음
    if (!savedBudClassIds.value.has(bud)) {
      showRevisionManageDialog.value = false;
      selectedRevisionTabId.value = "";
      revisionTabs.value = revisionTabs.value.filter((t) => t.id !== bud);
      showRevisionSnack(`${tabLabel(bud)} 빈 시트를 닫았습니다. (아직 저장된 행이 없습니다)`);
      return;
    }

    const res = await budgetApi.deleteYear({
      memberCode: memberCode.value,
      year: revisionYear.value,
      budClass: bud,
      bizGubun: bizGubun.value,
    });
    const data = res.data as { success?: boolean; message?: string };
    if (!data?.success) {
      showRevisionSnack(`추경 삭제 실패: ${data?.message || "알 수 없는 오류"}`);
      return;
    }
    showRevisionManageDialog.value = false;
    selectedRevisionTabId.value = "";
    await loadBudTabs();
    revisionTabs.value = revisionTabs.value.filter((t) => t.id !== bud);
    showRevisionSnack(`${tabLabel(bud)} 시트와 추경관리 입력을 삭제했습니다.`);
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    const st = err.response?.status;
    showRevisionSnack(`추경 삭제 실패: ${msg}${st ? ` (${st})` : ""}`);
  } finally {
    deleteRevisionLoading.value = false;
  }
}

function onBudgetSaved() {
  void loadBudTabs();
}

async function confirmAddRevision() {
  if (budgetPanelRef.value && !(await budgetPanelRef.value.confirmDiscard())) return;
  const to = nextBudClass.value;
  const fromLabel = previousSheetLabel.value;

  if (addRevisionMode.value === "empty") {
    addEmptyRevisionTabOnly();
    showRevisionManageDialog.value = false;
    return;
  }

  // 직전 시트만. 본예산이면 빈 차수, 추경이면 저장된 마지막 번호. 빈 로컬 탭은 원본이 아니다.
  const from = maxSavedNumericRevisionId.value > 0 ? String(maxSavedNumericRevisionId.value) : "";

  addRevisionLoading.value = true;
  try {
    const res = await budgetApi.cloneBudClass({
      memberCode: memberCode.value,
      year: revisionYear.value,
      budClassFrom: from,
      budClassTo: to,
      bizGubun: bizGubun.value,
    });
    const data = res.data;
    if (!data?.success) {
      const msg = data?.message || "알 수 없는 오류";
      showRevisionSnack(`추경 복사 실패: ${msg}`);
      return;
    }
    const copied = data.copied ?? 0;
    const warnings = Array.isArray(data.warnings) ? data.warnings : [];
    showRevisionManageDialog.value = false;
    await loadBudTabs();
    selectedRevisionTabId.value = to;
    if (warnings.length) {
      showRevisionSnack(
        `${to}차 추경에 ${fromLabel} 항목 ${copied}건을 그대로 넣었습니다. 아래 짝·그룹 점검 메시지를 확인하세요.`,
        warnings
      );
    } else {
      showRevisionSnack(
        `${to}차 추경에 ${fromLabel} 항목 ${copied}건을 그대로 넣었습니다.`
      );
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    const st = err.response?.status;
    showRevisionSnack(`추경 복사 실패: ${msg}${st ? ` (${st})` : ""}`);
  } finally {
    addRevisionLoading.value = false;
  }
}

watch([memberCode, bizGubun, revisionYear], () => {
  void loadBudTabs();
});

onMounted(() => {
  void loadBudTabs();
});
</script>
